/**
 * A play as a playbook diagram (slice 8c): a mini field strip drawn from the play JSON's aligns and
 * paths, like a page of a Madden playbook. Up is downfield. Offense: formation dots, routes in
 * yellow with the first read in red, blocks as blue "T"s, the carrier's path in red. Defense: the
 * offense's line and passer as hollow ghosts, rushers as red arrows, zones as shaded areas at
 * their landmarks, man coverage as a yellow line to the receiver across from him (where the sim
 * aligns a man defender). Pure DOM, no Phaser; the colors are the `--dg-*` tokens in book.css.
 *
 * Coordinates are the JSON's own `[downfield, lateral]` yards: x = lateral, y = −downfield, with
 * depth past 10 yards squeezed so a 35-yard go route still ends on the strip with its arrowhead.
 */
import type {
  DefenseAssignment,
  DefensePlayDef,
  DefenseSlot,
  OffenseAssignment,
  OffensePlayDef,
  PlayDef,
  Spot,
} from '@tdmons/sim';

const NS = 'http://www.w3.org/2000/svg';
/** Lateral ±20 yards, downfield from 17.5 (squeezed) to 7.5 behind the line. Drawing only. */
export const DIAGRAM_VIEWBOX = '-20 -17.5 40 25';
const SQUEEZE_FROM = 10;
const SQUEEZE = 0.25;
const DOT_R = 0.95;
const BLOCK_STEM = 1;
const BLOCK_BAR = 0.8;
const TARGET_LINE = 4;
const RUSH_LINE = 4.5;
const CARRY_ON = 3;
/** Where the QB stands for a rusher to aim at, and the ghost line on a defense diagram. */
const QB_SPOT: Spot = [-5, 0];
const GHOSTS: readonly Spot[] = [[-1, -1.5], [-1, 1.5], QB_SPOT];
/** Where each defender lines up in a base 2-2-2-1, for a blocker's second-level target. */
const BASE_DEFENSE: Record<DefenseSlot, Spot> = {
  DL1: [1, -1.5],
  DL2: [1, 1.5],
  LB1: [5, -3],
  LB2: [5, 3],
  CB1: [8, -16],
  CB2: [8, 16],
  S: [12, 0],
};
/** A zone's shaded area, [rx, ry] in yards, by its name in balance.json coverage.zones. */
const ZONE_SIZE: Record<string, [number, number]> = {
  'deep-third': [5.5, 3],
  'deep-middle': [6, 3],
  'short-middle': [4, 2.2],
  hook: [3.5, 2.2],
  'flat-left': [4, 2.2],
  'flat-right': [4, 2.2],
  box: [5, 2],
};
const ZONE_DEFAULT: [number, number] = [4, 2.2];

type Pt = readonly [number, number];

const depth = (d: number) => (d > SQUEEZE_FROM ? SQUEEZE_FROM + (d - SQUEEZE_FROM) * SQUEEZE : d);
/** A play spot to diagram coordinates. */
export const toDiagram = ([down, lateral]: Spot): Pt => [lateral, -depth(down)];

const r2 = (n: number) => Math.round(n * 100) / 100;
const pts = (ps: readonly Pt[]) => ps.map(([x, y]) => `${r2(x)},${r2(y)}`).join(' ');

function node<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs))
    e.setAttribute(k, String(typeof v === 'number' ? r2(v) : v));
  return e;
}

/** A point `len` along from `a` toward `b`. */
function toward(a: Pt, b: Pt, len: number): Pt {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const n = Math.hypot(dx, dy) || 1;
  return [a[0] + (dx / n) * len, a[1] + (dy / n) * len];
}

/** A short bar across the end of the line a→b (a block's "T"). */
function bar(a: Pt, b: Pt): string {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const n = Math.hypot(dx, dy) || 1;
  const px = (-dy / n) * BLOCK_BAR;
  const py = (dx / n) * BLOCK_BAR;
  return `M${r2(a[0])} ${r2(a[1])}L${r2(b[0])} ${r2(b[1])}M${r2(b[0] - px)} ${r2(b[1] - py)}L${r2(b[0] + px)} ${r2(b[1] + py)}`;
}

let made = 0;

/** The arrowheads this diagram's lines end in, one per color (ids unique per diagram). */
function markers(svg: SVGSVGElement): Record<'route' | 'read' | 'block', string> {
  const id = `dg${++made}`;
  const defs = node('defs', {});
  const out = { route: `${id}-route`, read: `${id}-read`, block: `${id}-block` };
  for (const [kind, mid] of Object.entries(out)) {
    const m = node('marker', {
      id: mid,
      viewBox: '0 0 10 10',
      refX: 6,
      refY: 5,
      markerWidth: 4,
      markerHeight: 4,
      orient: 'auto',
    });
    m.append(node('path', { d: 'M0 0L10 5L0 10z', class: `dg-head dg-head-${kind}` }));
    defs.append(m);
  }
  svg.append(defs);
  return out;
}

function field(svg: SVGSVGElement): void {
  svg.append(node('rect', { x: -20, y: -17.5, width: 40, height: 25, class: 'dg-field' }));
  for (const d of [-5, 5, 10, 20]) {
    const y = -depth(d);
    svg.append(node('line', { x1: -20, y1: y, x2: 20, y2: y, class: 'dg-yard' }));
  }
  svg.append(node('line', { x1: -20, y1: 0, x2: 20, y2: 0, class: 'dg-los' }));
}

function dot(at: Pt, slot: string, cls: string): SVGCircleElement {
  const c = node('circle', { cx: at[0], cy: at[1], r: DOT_R, class: cls });
  c.dataset.dot = slot;
  return c;
}

function offense(
  svg: SVGSVGElement,
  play: OffensePlayDef,
  head: Record<'route' | 'read' | 'block', string>,
): void {
  const lines: SVGElement[] = [];
  const entries = Object.entries(play.assignments) as [string, OffenseAssignment][];
  for (const [slot, a] of entries) {
    const at = toDiagram(a.align);
    const path = (a.path ?? []).map(toDiagram);
    if (a.role === 'route') {
      const read = play.reads[0] === slot;
      const kind = read ? 'read' : 'route';
      const l = node('polyline', {
        points: pts([at, ...path]),
        class: `dg-${kind}`,
        'marker-end': `url(#${head[kind]})`,
      });
      l.dataset.kind = kind;
      lines.push(l);
    } else if (a.role === 'carry') {
      const end = path.at(-1) ?? at;
      const l = node('polyline', { points: pts([at, ...path]), class: 'dg-read' });
      l.dataset.kind = 'carry';
      const on = node('line', {
        x1: end[0],
        y1: end[1],
        x2: end[0],
        y2: end[1] - CARRY_ON,
        class: 'dg-read dg-on',
        'marker-end': `url(#${head.read})`,
      });
      lines.push(l, on);
    } else if (a.role === 'pass' && path.length > 0) {
      lines.push(node('polyline', { points: pts([at, ...path]), class: 'dg-drop' }));
    } else if (a.role === 'block') {
      const to = a.target
        ? toward(at, toDiagram(BASE_DEFENSE[a.target]), TARGET_LINE)
        : ([at[0], at[1] - DOT_R - BLOCK_STEM] as Pt);
      const from = a.target ? at : ([at[0], at[1] - DOT_R] as Pt);
      const l = node('path', { d: bar(from, to), class: 'dg-block' });
      l.dataset.kind = 'block';
      lines.push(l);
    }
  }
  svg.append(...lines);
  for (const [slot, a] of entries) svg.append(dot(toDiagram(a.align), slot, 'dg-off'));
}

function defense(
  svg: SVGSVGElement,
  play: DefensePlayDef,
  head: Record<'route' | 'read' | 'block', string>,
): void {
  const entries = Object.entries(play.assignments) as [string, DefenseAssignment][];
  const areas: SVGElement[] = [];
  const lines: SVGElement[] = [];
  const ghosts: SVGElement[] = GHOSTS.map((s) => {
    const [x, y] = toDiagram(s);
    return node('circle', { cx: x, cy: y, r: DOT_R, class: 'dg-ghost' });
  });
  for (const [, a] of entries) {
    const at = toDiagram(a.align);
    if (a.role === 'rush' || a.role === 'blitz') {
      const end = toward(at, toDiagram(QB_SPOT), RUSH_LINE);
      const l = node('line', {
        x1: at[0],
        y1: at[1],
        x2: end[0],
        y2: end[1],
        class: 'dg-rush',
        'marker-end': `url(#${head.read})`,
      });
      l.dataset.kind = 'rush';
      lines.push(l);
    } else if (a.role === 'zone') {
      const land = a.drop ? toDiagram(a.drop) : at;
      const [rx, ry] = ZONE_SIZE[a.zone ?? ''] ?? ZONE_DEFAULT;
      const z = node('ellipse', { cx: land[0], cy: land[1], rx, ry, class: 'dg-zone' });
      z.dataset.kind = 'zone';
      areas.push(z);
      if (a.drop) {
        lines.push(
          node('line', { x1: at[0], y1: at[1], x2: land[0], y2: land[1], class: 'dg-zone-line' }),
        );
      }
    } else if (a.role === 'man') {
      // The receiver he covers, on the line across from him.
      const rec = toDiagram([-1, a.align[1]]);
      ghosts.push(node('circle', { cx: rec[0], cy: rec[1], r: DOT_R, class: 'dg-ghost' }));
      const l = node('line', {
        x1: at[0],
        y1: at[1],
        x2: rec[0],
        y2: rec[1] - DOT_R,
        class: 'dg-man',
      });
      l.dataset.kind = 'man';
      lines.push(l);
    }
  }
  svg.append(...areas, ...ghosts, ...lines);
  for (const [slot, a] of entries) svg.append(dot(toDiagram(a.align), slot, 'dg-def'));
}

export function playDiagram(play: PlayDef): SVGSVGElement {
  const svg = node('svg', { viewBox: DIAGRAM_VIEWBOX, class: 'play-diagram' });
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const head = markers(svg);
  field(svg);
  if (play.side === 'offense') offense(svg, play, head);
  else defense(svg, play, head);
  svg.append(node('ellipse', { cx: 0, cy: 0, rx: 0.6, ry: 0.4, class: 'dg-ball' }));
  return svg;
}
